import type React from 'react'
import type {
  AuthCodeRef,
  FirstFieldUpdateProps,
  HandlersProps,
} from '../PINCodeInput.h'

import { useCallback, useEffect, useRef } from 'react'

const inputPattern = '[0-9]{1}'

export const useHandlers = ({
  sendResult,
  inputsRef,
  setIsFocused,
  lengthPinCode,
}: HandlersProps) => {
  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const {
        target: { value, nextElementSibling },
      } = e

      const focusInput = () => {
        if (nextElementSibling !== null) {
          ;(nextElementSibling as HTMLInputElement).focus()
        }
      }

      if (value.length > 1) {
        e.target.value = value.charAt(0)
        focusInput()
      } else if (value.match(inputPattern)) {
        focusInput()
      } else {
        e.target.value = ''
      }

      setIsFocused(false)
      sendResult()
    },
    []
  )

  const handleOnKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const { key } = e
      const target = e.target as HTMLInputElement
      if (key === 'Backspace') {
        if (target.value === '') {
          if (target.previousElementSibling !== null) {
            const t = target.previousElementSibling as HTMLInputElement
            t.value = ''
            t.focus()
            e.preventDefault()
          }
        } else {
          target.value = ''
        }
        sendResult()
      }
    },
    []
  )

  const handleOnFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select()
  }, [])

  const handleOnPaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedValue = e.clipboardData.getData('Text')
      const validPastedValue =
        pastedValue.match(`^\\d{${lengthPinCode}}`) &&
        pastedValue.length >= lengthPinCode
          ? pastedValue
          : ''

      let currentInput = 0

      for (let i = 0; i < validPastedValue.length; i++) {
        const pastedCharacter = validPastedValue.charAt(i)
        const currentValue = inputsRef.current[currentInput].value
        if (!currentValue) {
          inputsRef.current[currentInput].value = pastedCharacter
          if (inputsRef.current[currentInput].nextElementSibling !== null) {
            ;(
              inputsRef.current[currentInput]
                .nextElementSibling as HTMLInputElement
            ).focus()
            currentInput++
          }
        }
      }
      sendResult()

      e.preventDefault()
    },
    [lengthPinCode, inputsRef]
  )
  return { handleOnChange, handleOnKeyDown, handleOnFocus, handleOnPaste }
}

export const useClearPopup = ({ invalid }: { invalid?: boolean }) => {
  const inputRef = useRef<AuthCodeRef>(null)

  useEffect(() => {
    if (invalid) {
      inputRef.current?.clear()
    }
  }, [invalid])

  return { inputRef }
}

// Needed to fix the re-entry of the first character if the popup is used as a children or an extra rerender occurs when changing external props
export const useUpdateFirstField = ({
  valueAfterInvalid,
  inputsRef,
  setIsFocused,
}: FirstFieldUpdateProps): void => {
  const firstValue = valueAfterInvalid.length <= 1 ? valueAfterInvalid : ''

  useEffect(() => {
    if (firstValue) {
      inputsRef.current[0].value = firstValue
      inputsRef.current[1].focus()
      setIsFocused(false)
    }
  }, [firstValue])
}
